'use client'

import { memo, useCallback } from 'react'
import { HardDrive, Mic, ShieldCheck, Sparkles, Terminal } from 'lucide-react'
import { Ninxl8BitThemeProvider } from '@/app/(marketing)/_components/8bit'
import '../craft.css'

import { CraftNav } from '../_components/craft-nav'
import { CraftHero } from '../_components/craft-hero'
import { CraftUseCases } from '../_components/craft-use-cases'
import { CraftJourney } from '../_components/craft-journey'
import { CraftInkflow } from '../_components/craft-inkflow'
import { CraftWorksIn } from '../_components/craft-works-in'
import { CraftDownload } from '../_components/craft-download'
import { CraftFooter } from '../_components/craft-footer'
import { CraftSection, CraftDivider } from '../_components/craft-section'
import { CraftTrustStrip } from '../_components/craft-trust-strip'
import { CraftValueProps } from '../_components/craft-value-props'
import { CraftDeepDive } from '../_components/craft-deep-dive'
import { CRAFT_MEDIA } from '../_components/craft-media'

import { NinxlPostHeroStart } from '@/app/ninxl/_components/ninxl-post-hero-start'
import { NinxlScrollWorldDemo } from '@/app/ninxl/_components/ninxl-scroll-world-demo'
import { NinxlComparisonTable } from '@/app/ninxl/_components/ninxl-comparison-table'
import { NinxlLocalByDesign } from '@/app/ninxl/_components/ninxl-local-by-design'
import { NinxlFeatureHighlights } from '@/app/ninxl/_components/ninxl-feature-highlights'
import { NinxlFeatureCarousel } from '@/app/ninxl/_components/ninxl-feature-carousel'
import { ContinueAnywhereSection } from '@/app/ninxl/_components/continue-anywhere-section'
import { NinxlIntegrationMarquee } from '@/app/ninxl/_components/ninxl-integration-marquee'
import { NinxlPricingSection } from '@/app/ninxl/_components/ninxl-pricing-section'
import { NinxlFaqSection } from '@/app/ninxl/_components/ninxl-faq-section'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'
import { NinxlMagneticCta } from '@/app/ninxl/_components/ninxl-hero-entrance'
import { NinxlSectionHeader } from '@/app/ninxl/_components/ninxl-graphics'

import { NinContactSection } from '@/app/(marketing)/_components/nin-contact-section'
import { NinConnectPlatformSection } from '@/app/(marketing)/_components/nin-connect-platform-section'
import { MarketingContentMarquee } from '@/app/(marketing)/_components/marketing-content-marquee'
import { CmsLandingHighlights } from '@/app/(marketing)/_components/cms/cms-landing-highlights'

const NINXL_CONTENT_TILES = [
  { id: 'private', title: 'Private by default', subtitle: 'No silent cloud hop', icon: ShieldCheck },
  { id: 'whisper', title: 'On-device Whisper', subtitle: 'InkFlow dictation', icon: Mic },
  { id: 'shell', title: 'Shell + browser', subtitle: 'Tools you approve', icon: Terminal },
  { id: 'local', title: 'Local models', subtitle: 'Warm on your disk', icon: HardDrive },
  { id: 'crews', title: 'Crews', subtitle: 'Automations with consent', icon: Sparkles },
] as const

/**
 * baby: Craft v2: full classic /ninxl section parity, craft paper style only.
 * Header/footer keep the same buttons & destinations; visuals change.
 */
const NinxlCraftPageContent = memo(function NinxlCraftPageContent() {
  const scrollTo = useCallback((id: string) => {
    const target =
      document.getElementById(id) ??
      document.querySelector<HTMLElement>(`[data-section="${id}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    // baby: ninxl-8bit class is required for theme CSS tokens on classic sections.
    // Craft CSS overrides pixel look while keeping light theme variables.
    <div className="ninxl-craft ninxl-landing ninxl-8bit" data-ninxl-theme="light">
      {/* baby: Soft Higgsfield atmosphere under the edge collage: decorative only. */}
      <video
        className="ninxl-craft__atmosphere"
        src={CRAFT_MEDIA.atmosphereVideo}
        muted
        playsInline
        autoPlay
        loop
        preload="none"
        aria-hidden
      />
      <div className="ninxl-craft__edge" aria-hidden />
      <div className="ninxl-craft__grain" aria-hidden />

      <div className="ninxl-craft__content">
        <CraftNav onScrollTo={scrollTo} />

        {/* Hero → Adam scroll → Journey constellation (blended into kraft) → use cases */}
        <CraftHero />
        <CraftJourney />
        <CraftUseCases />

        <CraftDivider motif="plane" />
        <NinxlPostHeroStart />
        <NinxlScrollWorldDemo />

        {/* Download: same id for nav CTA */}
        <CraftSection id="download" data-section="download" className="py-10">
          <NinxlScrollReveal variant="blur">
            <CraftDownload />
          </NinxlScrollReveal>
          <NinxlScrollReveal delay={0.12} className="mt-8">
            <CraftTrustStrip />
          </NinxlScrollReveal>
        </CraftSection>

        <CraftDivider motif="star" />
        <CmsLandingHighlights />

        <CraftDivider motif="tape" />
        <CraftSection id="discover" data-section="discover">
          <CraftValueProps />
        </CraftSection>

        <CraftDivider motif="plane" />
        <CraftSection alt data-section="expand">
          <CraftDeepDive onScrollTo={scrollTo} />
        </CraftSection>

        {/* baby: Demos carousel earlier: after product pitch, before compare/story stack. */}
        <CraftDivider motif="tape" />
        <CraftSection id="features" alt data-section="features">
          <NinxlSectionHeader
            label="REAL CAPABILITIES"
            title="Powerful local tools that just work."
            description="Chat, on-device voice, and tools run on your machine. No data sent unless you explicitly approve a cloud plugin."
            className="mb-8"
          />
          <NinxlFeatureHighlights />
          <NinxlScrollReveal delay={0.12} variant="blur" className="mt-12">
            <div className="craft-download__card">
              <p className="craft-section__eyebrow mb-2 text-center">Demos</p>
              <p className="mb-6 text-center text-[var(--craft-mute)]">
                Real demos across Gmail, brokers, shell, and more
              </p>
              <NinxlFeatureCarousel />
            </div>
          </NinxlScrollReveal>
        </CraftSection>

        <CraftDivider motif="star" />
        <CraftSection data-section="content-marquee" className="py-10 md:py-14">
          <MarketingContentMarquee label="NINX capabilities" tiles={NINXL_CONTENT_TILES} />
        </CraftSection>

        <CraftDivider motif="plane" />
        <CraftSection id="compare" alt data-section="compare" className="py-12 md:py-14">
          <NinxlSectionHeader
            label="How NINX stacks up"
            title="NINX vs popular AI desktops"
            description="Local-first private agent with on-device voice, next to Codex, Perplexity, ChatGPT, and Claude."
            className="mb-6"
          />
          <NinxlScrollReveal delay={0.2} variant="pop">
            <NinxlComparisonTable />
          </NinxlScrollReveal>
        </CraftSection>

        <CraftDivider motif="star" />
        <CraftSection data-section="foundation">
          <NinxlLocalByDesign />
        </CraftSection>

        <CraftDivider motif="plane" />
        <CraftSection id="inkflow" data-section="inkflow">
          {/* baby: Half Whisper ribbon + half compact demo, not a full-bleed autoplay stack. */}
          <CraftInkflow />
        </CraftSection>

        <CraftDivider motif="star" />
        <CraftSection id="works-in" data-section="works-in" className="scroll-mt-24 py-16">
          {/* baby: Craft papercraft Works In: replaces pixel WorksInAppsSection. */}
          <CraftWorksIn />
        </CraftSection>

        <CraftDivider motif="tape" />
        <CraftSection data-section="everywhere">
          <ContinueAnywhereSection />
        </CraftSection>

        <CraftDivider motif="plane" />
        <CraftSection id="connect-platform" data-section="connect-platform" className="scroll-mt-24">
          <NinConnectPlatformSection variant="product" />
        </CraftSection>

        <CraftDivider motif="star" />
        <CraftSection alt data-section="connect">
          <NinxlSectionHeader
            label="Deep integrations"
            title="Works where your work already lives."
            description="Gmail, brokers, shell, browser agents, and MCP servers, connected locally with your approval."
            className="mb-8"
          />
          <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
            {['Email & calendar', 'Finance & trading', 'Dev & shell', 'Browser automation'].map(
              (category) => (
                <span key={category} className="craft-chip">
                  {category}
                </span>
              ),
            )}
          </div>
          <NinxlIntegrationMarquee />
        </CraftSection>

        <CraftDivider motif="tape" />
        <CraftSection id="pricing" data-section="pricing">
          <NinxlPricingSection />
        </CraftSection>

        <CraftDivider motif="plane" />
        <CraftSection alt data-section="faq" id="faq">
          <NinxlFaqSection />
        </CraftSection>

        <CraftDivider motif="star" />
        <CraftSection id="contact" data-section="contact" className="scroll-mt-24 py-16">
          <NinContactSection variant="product" />
        </CraftSection>

        <CraftDivider motif="tape" />
        <CraftSection className="py-24 text-center" data-section="commit">
          <NinxlScrollReveal variant="scale">
            <div className="craft-download__card mx-auto max-w-md">
              <p className="craft-section__eyebrow">YOUR DESKTOP AWAITS</p>
              <h2 className="craft-section__title" style={{ maxWidth: 'none', margin: '0 auto 0.75rem' }}>
                Get NINX on your desktop.
              </h2>
              <p className="craft-section__body" style={{ margin: '0 auto 1.5rem' }}>
                The journey ends where your private AI begins. Download in seconds.
              </p>
              <NinxlMagneticCta>
                <button
                  type="button"
                  onClick={() => scrollTo('download')}
                  className="craft-btn craft-btn--primary"
                >
                  Download NINX
                </button>
              </NinxlMagneticCta>
              <p className="mt-4 text-xs tracking-wide text-[var(--craft-mute)]">
                Mac (Apple Silicon) · Windows · Free to try
              </p>
            </div>
          </NinxlScrollReveal>
        </CraftSection>

        <CraftFooter />
      </div>
    </div>
  )
})

const NinxlCraftPage = memo(function NinxlCraftPage() {
  return (
    <Ninxl8BitThemeProvider>
      <NinxlCraftPageContent />
    </Ninxl8BitThemeProvider>
  )
})

export default NinxlCraftPage
